import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { DailyRoutineDetails } from '../../application/dto/DailyRoutineDetails';
import type { DailyMissionsClient } from '../../application/services/DailyMissionsService';
import { CircularProgress } from '../components/CircularProgress';
import { MissionCard } from '../components/MissionCard';
import { ErrorState, LoadingState } from '../components/States';
import { PageHeader } from '../components/PageHeader';
import { useTranslation } from '../design-system/data/translations';
import { MODE_LABELS } from '../design/modeCopy';
import { ChevronIcon } from '../icons';

function formatDate(logicalDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${logicalDate}T00:00:00.000Z`));
}

export function HistoryDetailPage({ client }: { readonly client: DailyMissionsClient }) {
  const { t, locale } = useTranslation();
  const { logicalDate = '' } = useParams();
  const [details, setDetails] = useState<DailyRoutineDetails>();
  const [error, setError] = useState<string>();
  const load = useCallback(async () => {
    const result = await client.getByDate(logicalDate);
    if (result.ok) setDetails(result.value);
    else setError(result.error.message);
  }, [client, logicalDate]);
  useEffect(() => {
    let active = true;
    void client.getByDate(logicalDate).then((result) => {
      if (!active) return;
      if (result.ok) setDetails(result.value);
      else setError(result.error.message);
    });
    return () => {
      active = false;
    };
  }, [client, logicalDate]);

  if (error)
    return (
      <main className="page page--center">
        <ErrorState message={error} onRetry={() => void load()} />
      </main>
    );
  if (!details)
    return (
      <main className="page page--center">
        <LoadingState label={t('history.detail_loading')} />
      </main>
    );

  return (
    <main className="page history-detail-page">
      <Link className="back-link" to="/history">
        <ChevronIcon className="back-link__icon" aria-hidden="true" />
        {t('history.back')}
      </Link>
      <PageHeader
        eyebrow={t('history.readonly')}
        title={formatDate(details.routine.logicalDate, locale)}
        description={`Mode ${MODE_LABELS[details.routine.dayMode]} · ${
          details.routine.status === 'completed' ? t('history.day_completed') : t('history.day_open')
        }`}
      />
      <section className="history-progress" aria-label={t('history.detail_progress_aria')}>
        <CircularProgress value={details.progress.percentage} label={t('history.progress_label')} />
        <span>
          {t('history.missions_completed')
            .replace('{completed}', String(details.progress.completedCount))
            .replace('{active}', String(details.progress.activeCount))}
        </span>
      </section>
      <section className="mission-list" aria-label={t('history.readonly_missions_aria')}>
        {details.steps.map((step) => (
          <MissionCard key={step.id} step={step} busy={false} readOnly />
        ))}
      </section>
    </main>
  );
}
